import React, { useEffect, useState, useCallback } from 'react';
import { Tabs, Tab, Spinner } from 'react-bootstrap';
import { message } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { feedbackService, FeedbackPost, FeedbackComment } from '../../services/feedback/feedbackService';
import { getNotifications, Notification } from '../../services/api';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getDemoMyFeedbackBundle } from '../../demo/demoBrowseMocks';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';
import { feedbackCategoryPill, feedbackStatusPill } from './feedbackUi';

const MyFeedbackRecords: React.FC = () => {
  const { isDemoBrowseOnly } = useAuth();
  const [posts, setPosts] = useState<FeedbackPost[]>([]);
  const [comments, setComments] = useState<FeedbackComment[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadPostIds, setUnreadPostIds] = useState<string[]>([]);
  const [tabKey, setTabKey] = useState('posts');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (isDemoBrowseOnly) {
        const bundle = getDemoMyFeedbackBundle();
        setPosts(bundle.posts.items);
        setComments(bundle.comments.items);
        setReports(bundle.reports.items || []);
        setUnreadPostIds(['demo-fb-1']);
        return;
      }
      const [postResult, commentResult, reportResult, notifRes] = await Promise.all([
        feedbackService.listMyPosts({ page: 1, pageSize: 100 }),
        feedbackService.listMyComments({ page: 1, pageSize: 100 }),
        feedbackService.listMyReports({ page: 1, pageSize: 100 }),
        getNotifications({ type: 'feedback', isRead: false }),
      ]);
      setPosts(postResult.items);
      setComments(commentResult.items);
      setReports(reportResult.items || []);

      if (notifRes.success && notifRes.data) {
        const notifications = notifRes.data.notifications as Notification[];
        const postIds = notifications
          .filter((n) => n.relatedResourceType === 'feedback_post' && n.relatedResource)
          .map((n) => String(n.relatedResource));
        setUnreadPostIds(Array.from(new Set(postIds)));
      }
    } catch (error: any) {
      message.error(error.message || '获取我的匿名记录失败');
    } finally {
      setLoading(false);
    }
  }, [isDemoBrowseOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const reportStatusPill = (status: string) => {
    const cls =
      status === 'processed'
        ? 'rm-fb-status-pill rm-fb-status-pill--resolved'
        : status === 'pending'
          ? 'rm-fb-status-pill rm-fb-status-pill--pending'
          : 'rm-fb-status-pill rm-fb-status-pill--processing';
    const text = status === 'processed' ? '已处理' : status === 'pending' ? '待处理' : '处理中';
    return <span className={cls}>{text}</span>;
  };

  return (
    <div className="page-container full-height members-lims fb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <Link to="/feedback/square" className="fb-crumb-link">
          畅所欲言
        </Link>
        <span className="mm-crumb-sep">/</span>
        <span aria-current="page">我的记录</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">我的匿名记录</h1>
          <p className="mm-subtitle mb-0">仅你本人可见：跟踪帖子处理进度、评论与举报结果。</p>
        </div>
      </header>

      <Tabs activeKey={tabKey} onSelect={(k) => setTabKey(k || 'posts')} className="mb-2 border-0 lims-resource-tabs">
        <Tab eventKey="posts" title="我发布的帖子">
          <div className="fb-detail-panel">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-muted text-center py-4">暂无帖子</div>
            ) : (
              <ul className="fb-list-plain">
                {posts.map((item) => (
                  <li
                    key={item._id}
                    role="button"
                    tabIndex={0}
                    className="align-items-start"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/feedback/post/${item._id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/feedback/post/${item._id}`)}
                  >
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <span className="fw-bold text-break">{item.title}</span>
                        {feedbackCategoryPill(item.category)}
                        {feedbackStatusPill(item.status)}
                        {unreadPostIds.includes(item._id) && (
                          <span className="rm-fb-status-pill rm-fb-status-pill--processing">有新进展</span>
                        )}
                      </div>
                      <div className="small text-muted">
                        {new Date(item.createdAt).toLocaleString('zh-CN')} · {item.likeCount} 赞同 · {item.commentCount} 评论
                      </div>
                    </div>
                    <LimsNavIcon name="chevron_right" sizePx={22} className="text-muted flex-shrink-0" />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Tab>
        <Tab eventKey="comments" title="我的匿名评论">
          <div className="fb-detail-panel">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : comments.length === 0 ? (
              <div className="text-muted text-center py-4">暂无评论</div>
            ) : (
              <ul className="fb-list-plain">
                {comments.map((item) => (
                  <li key={item._id} className="align-items-start flex-column">
                    <div className="fw-semibold">{item.anonymousName}</div>
                    <div className="small text-muted mb-1">{new Date(item.createdAt).toLocaleString('zh-CN')}</div>
                    <div className="text-break">{item.content}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Tab>
        <Tab eventKey="reports" title="我的举报">
          <div className="fb-detail-panel">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-muted text-center py-4">暂无举报</div>
            ) : (
              <ul className="fb-list-plain">
                {reports.map((item) => (
                  <li key={item._id} className="flex-column align-items-stretch text-start">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className="fw-semibold">{item.reasonType}</span>
                      {reportStatusPill(item.status)}
                    </div>
                    <div className="mt-1">{item.reasonDetail || '无详细说明'}</div>
                    <div className="small text-muted mt-1">{new Date(item.createdAt).toLocaleString('zh-CN')}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default MyFeedbackRecords;
